import { useCartStore } from "../../store/cart-store";
import { useEffect, useRef } from "react";
import { sendTrackingEvent } from "../../lib/tracking";
import { useTranslations } from "../../i18n";

type CartSuccessScreenProps = {
  apiUrl: string;
};

export function CartSuccessScreen({ apiUrl }: CartSuccessScreenProps) {
  const { cartNumber, bookingIds, isRequestBooking, cart, resetCart } =
    useCartStore();
  const { t } = useTranslations();
  const purchaseTracked = useRef(false);

  // Tracking purchase à l'affichage de la page de confirmation
  useEffect(() => {
    if (purchaseTracked.current || !bookingIds || bookingIds.length === 0)
      return;
    purchaseTracked.current = true;

    const bookings = cart?.bookings ?? [];
    const totalValue = bookings.reduce((acc, b) => acc + b.itemsTotal, 0);
    const items = bookings.flatMap((b) =>
      b.bookingItems.map((bi) => ({
        item_id: b.id,
        item_name: b.productTitle,
        item_category: b.optionName,
        item_category2: bi.unitName,
        item_variant: b.eventDate || undefined,
        price:
          ((bi.promotionPrice ?? 0) > 0
            ? (bi.promotionPrice ?? 0)
            : (bi.price ?? 0)) / 100,
        quantity: bi.quantity,
      })),
    );

    sendTrackingEvent("purchase", null, {
      transaction_id: cartNumber || "",
      cart_id: cart?.id || "",
      currency: "EUR",
      value: totalValue / 100,
      items,
    });
  }, [bookingIds, cartNumber, cart]);

  if (!bookingIds || bookingIds.length === 0) {
    return null;
  }

  const handleDownloadTickets = (bookingId: string) => {
    const ticketUrl = `${apiUrl}/api/ticket/${bookingId}`;
    window.open(ticketUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadGift = (bookingId: string) => {
    const giftUrl = `${apiUrl}/api/gift/${bookingId}`;
    window.open(giftUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadInvoice = (bookingId: string) => {
    const invoiceUrl = `${apiUrl}/api/invoice/presta/${bookingId}`;
    window.open(invoiceUrl, "_blank", "noopener,noreferrer");
  };

  const isBookingGift = (bookingId: string) => {
    return cart?.bookings.some((b) => b.id === bookingId && b.isGift) ?? false;
  };

  const handleContinueShopping = () => {
    resetCart();
    window.location.href = "/activites";
  };

  // Affichage pour les réservations REQUEST (en attente de validation)
  if (isRequestBooking) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 rounded-xl bg-white p-2 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Icône d'attente */}
        <div className="flex size-16 sm:size-20 items-center justify-center rounded-full bg-orange-100">
          <svg
            className="size-8 sm:size-10 text-orange-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Message de confirmation */}
        <div className="text-center">
          <h2 className="mb-2 text-xl sm:text-2xl font-bold text-gray-900">
            {t.success.requestTitle}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-sm">
            {t.success.requestMessage}
          </p>
          {cartNumber && (
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
              {t.success.requestNumber}{" "}
              <span className="font-semibold">{cartNumber}</span>
            </p>
          )}
        </div>

        {/* Note informative */}
        <div className="rounded-lg bg-orange-50 p-3 sm:p-4 text-xs sm:text-sm text-orange-800 border border-orange-100 max-w-sm">
          <p className="flex items-start gap-2">
            <svg
              className="size-5 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <span dangerouslySetInnerHTML={{ __html: t.success.requestNotice }} />
          </p>
        </div>

        {/* Bouton continuer */}
        <div className="flex w-full flex-col gap-2 sm:gap-3">
          <button
            onClick={handleContinueShopping}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 hover:shadow-xl hover:-translate-y-0.5"
          >
            <svg
              className="size-4 sm:size-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
              />
            </svg>
            {t.common.continueShopping}
          </button>
        </div>
      </div>
    );
  }

  // Affichage pour les réservations INSTANT et les bons cadeaux
  return (
    <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 rounded-xl bg-white p-2 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Icône de succès */}
      <div className="flex size-16 sm:size-20 items-center justify-center rounded-full bg-green-100">
        <svg
          className="size-8 sm:size-10 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      {/* Message de confirmation */}
      <div className="text-center">
        <h2 className="mb-2 text-xl sm:text-2xl font-bold text-gray-900">
          {t.success.orderConfirmedTitle}
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          {t.success.orderConfirmedMessage}
        </p>
        {cartNumber && (
          <p className="mt-2 text-xs sm:text-sm text-gray-500">
            {t.success.orderNumber}{" "}
            <span className="font-semibold">{cartNumber}</span>
          </p>
        )}
      </div>

      {/* Boutons d'action par réservation */}
      <div className="flex w-full flex-col gap-2 sm:gap-3">
        {bookingIds.map((bookingId) => {
          const booking = cart?.bookings.find((b) => b.id === bookingId);
          return (
            <div
              className="flex w-full flex-col gap-2 sm:gap-3 border rounded-md p-3 sm:p-4"
              key={bookingId}
            >
              {booking && (
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {booking.productTitle}
                  {booking.optionName ? ` — ${booking.optionName}` : ""}
                </p>
              )}
              {isBookingGift(bookingId) ? (
                <button
                  onClick={() => handleDownloadGift(bookingId)}
                  className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 hover:shadow-xl hover:-translate-y-0.5"
                >
                  <svg
                    className="size-4 sm:size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                    />
                  </svg>
                  {t.success.downloadGift}
                </button>
              ) : (
                <button
                  onClick={() => handleDownloadTickets(bookingId)}
                  className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 hover:shadow-xl hover:-translate-y-0.5"
                >
                  <svg
                    className="size-4 sm:size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
                    />
                  </svg>
                  {t.success.downloadTickets}
                </button>
              )}
              <button
                onClick={() => handleDownloadInvoice(bookingId)}
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
              >
                <svg
                  className="size-4 sm:size-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
                {t.success.downloadInvoice}
              </button>
            </div>
          );
        })}

        {/* Bouton continuer mes achats */}
        <button
          onClick={handleContinueShopping}
          className="flex items-center justify-center gap-2 rounded-xl border-2 border-brand-200 bg-brand-50 px-4 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-semibold text-brand-700 transition-all hover:border-brand-300 hover:bg-brand-100"
        >
          <svg
            className="size-4 sm:size-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
            />
          </svg>
          Continuer mes achats
        </button>
      </div>

      {/* Note informative */}
      <div className="mt-1 sm:mt-2 rounded-lg bg-blue-50 p-2.5 sm:p-3 text-xs sm:text-sm text-blue-800 border border-blue-100">
        <p className="flex items-start gap-2">
          <svg
            className="size-5 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          <span>
            {bookingIds.some((id) => isBookingGift(id))
              ? t.success.emailConfirmationGiftPlural
              : t.success.emailConfirmationTickets}
          </span>
        </p>
      </div>
    </div>
  );
}
